import { Component, ojs, app, v, payload } from "modular-openscriptjs";
import { monetary, formatThousand, cleanDispose, either } from "../../helpers";
import { tool, dom } from "../../fotastart";
import ProductImageView from "./ProductImageView";
import { dc } from "../../declarations";

const h = app("h");
const gc = app("gc");
const uc = app("uc");
const broker = app("broker");

ojs(ProductImageView);

export default class OrderDetailProductView extends Component {
	imagesModal = null;

	render(product, item, order, ...args) {
		if (!product?.id) return h.empty();

		const currency = order?.currency || dc.userCurrency || "KES";

		return h.div(
			{
				class: "d-flex p-0 gap-0 position-relative flex-column justify-content-between",
				data_product_id: product?.id,
			},

			// Main Product Block
			h.div(
				{ class: "d-flex gap-3 align-items-start mb-2" },

				// Product Image (Left)
				h.div(
					{ class: "flex-shrink-0" },
					either(
						product?.images && product?.images.length > 0,
						() =>
							h.img({
								src: product.images[0]?.path,
								class: "rounded-3 object-fit-cover border border-light-subtle shadow-sm cursor-pointer",
								style: "width: 70px; height: 70px;",
								loading: "lazy",
								onclick: this.method("seeImages", [
									product?.images,
								]),
							}),
						() =>
							h.div(
								{
									class: "bg-light rounded-3 d-flex align-items-center justify-content-center text-secondary border border-light-subtle",
									style: "width: 70px; height: 70px;",
								},
								h.i({ class: "bi bi-box-seam fs-3" })
							)
					)
				),

				// Product Info (Right)
				h.div(
					{ class: "flex-grow-1 min-w-0" },
					h.h6(
						{ class: "fw-bold text-dark text-truncate mb-1 text-wrap" },
						product?.name
					),

					// Processing Time
					either(product?.processing_time, () =>
						h.div(
							{ class: "text-muted mb-1 d-flex align-items-center gap-1", style: "font-size: 0.75rem;" },
							h.i({ class: "bi bi-clock text-primary" }),
							`Prep: ${product.processing_time}`
						)
					),

					// Price Paid & Quantity
					h.div(
						{ class: "mb-1 d-flex align-items-center gap-2" },
						h.span(
							{ class: "text-primary fw-bold" },
							monetary(item?.total_paid, currency)
						),
						h.span(
							{ class: "badge bg-light text-dark border rounded-pill px-2 py-1" },
							`x${item?.quantity ?? 1}`
						)
					),

					// Small print: how much of what you paid was for packaging - the total
					// above already includes it, this is just a breakdown for transparency.
					either(item?.package_price > 0, () =>
						h.div(
							{ class: "small text-muted mb-1" },
							`Includes `,
							monetary(item?.package_price, currency),
							` for packaging`
						)
					),

					// Purchase Price (Spent)
					either(
						item?.purchased == 1 || item?.purchase_price > 0,
						() =>
							h.div(
								h.div(
									{ class: "small text-success fw-semibold mt-1 d-flex align-items-center gap-1" },
									h.i({ class: "bi bi-check-circle-fill text-success" }),
									`Spent: `,
									h.span({ class: "fw-bold" }, monetary(item?.final_purchase_price ?? item?.purchase_price ?? item?.price ?? 0, currency))
								),

								// Small print: how much of what the deliverer spent was for
								// packaging. If the business didn't actually charge for packaging
								// this run (e.g. gave a free bag instead of a container), this is
								// correctly 0/omitted even if the customer was charged for one -
								// the difference is refunded back to the customer separately.
								either(item?.package_purchase_price > 0, () =>
									h.div(
										{ class: "small text-muted" },
										`Includes `,
										monetary(item?.package_purchase_price, currency),
										` for packaging`
									)
								)
							),
						() =>
							h.div(
								{ class: "small text-muted fw-semibold mt-1 d-flex align-items-center gap-1" },
								h.i({ class: "bi bi-hourglass-split text-secondary animate-pulse" }),
								"Pending purchase"
							)
					)
				)
			),

			// Features
			h.call(() => {
				let features = item?.features || [];
				let grouped = {};
				features.forEach((feat) => {
					let name = feat.feature_name || "";
					let val = feat.feature_value || "";
					if (!grouped[name]) {
						grouped[name] = [];
					}
					grouped[name].push(val);
				});

				let elements = [];
				Object.keys(grouped).forEach((name) => {
					let valuesString = grouped[name].join(", ");
					elements.push(
						h.div(
							{
								class: "badge bg-secondary-subtle text-secondary-emphasis border-0 me-1 mb-1 px-2.5 py-1.5 rounded-pill text-wrap small fw-normal",
								style: "max-width: 100%;",
							},
							name
								? h.span(
									{
										class: "text-muted fw-bold text-uppercase small me-1",
										style: "font-size: 0.7rem;",
									},
									`${name}:`
								)
								: h.empty(),
							h.span(
								{
									class: "fw-semibold text-primary text-wrap",
								},
								valuesString
							)
						)
					);
				});
				return elements.length > 0
					? h.div(
						{ class: "d-flex flex-wrap align-items-center mt-2 pt-2 border-top border-light-subtle" },
						...elements
					)
					: h.empty();
			}),

			// Extras
			h.call(() => {
				let extras = item?.extras || [];
				let elements = [];
				extras.forEach((ext) => {
					let extName = ext.extra_name || ext.name || ext.extra?.name || `Extra #${ext.extra_id || ext.id}`;
					let qty = ext.qty || ext.quantity || 1;

					elements.push(
						h.div(
							{
								class: "text-muted small mb-1 lh-sm text-wrap",
							},
							h.i({
								class: "fas fa-plus-circle text-success me-1",
							}),
							`${qty}x ${extName}`
						)
					);
				});
				return elements.length > 0
					? h.div({ class: "mt-2 pt-2 border-top border-light-subtle" }, ...elements)
					: h.empty();
			}),

			...args
		);
	}

	seeImages(images) {
		if (this.imagesModal) {
			cleanDispose(this.imagesModal);
		}

		this.imagesModal = tool.plainModal(
			h.div(
				{
					class: "modal-content border-0 w-auto mx-auto bg-transparent shadow-none",
				},
				h.div(
					{ class: "modal-body p-0 px-3" },
					h.ProductImageView(images)
				)
			),
			{}
		);

		this.imagesModal.show();
	}

	cleanUp() {
		super.cleanUp();
		cleanDispose(this.imagesModal);
	}
}

ojs(OrderDetailProductView);
